import { Banknote, CalendarDays, CreditCard, Percent, Truck, Wifi } from 'lucide-react';
import type { ProposalPayment } from '@/api/commercial/proposals';
import {
  fmtCents,
  ResumeCard,
  ResumeCardHeader,
  SummaryRow,
} from '@/components/commercial/proposals/shared/summary-components';
import { Separator } from '@/components/ui/separator';
import type { ICartItemBreakdown, ICartPaymentSummary } from '@/types/commercial/cart';
import type { CartSummaryCategories } from '@/utils/commercial/cart-summary-calculations';

function formatIsoDatePtBr(iso?: string | null): string | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [year, month, day] = iso.split('-');
  return `${day}/${month}/${year}`;
}

interface ConditionItemData {
  key: string;
  icon: React.ReactNode;
  title: string;
  value: string;
  info?: string[];
}

function ConditionItem({ icon, title, value, info }: Omit<ConditionItemData, 'key'>) {
  return (
    <div className="flex min-w-0 items-start gap-3 py-3.5">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-50">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <span className="min-w-0 break-words text-sm font-semibold text-gray-700">{title}</span>
          <span className="shrink-0 text-right text-sm font-bold tabular-nums text-gray-950">
            {value}
          </span>
        </div>
        {info && info.length > 0 && (
          <div className="mt-1 space-y-0.5">
            {info.map((line) => (
              <p key={line} className="text-xs text-gray-500">
                {line}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface ResumePaymentConditionsCardProps {
  payment: ProposalPayment | null;
  breakdown: ICartItemBreakdown | null;
  cartSummaryCategories: CartSummaryCategories;
  paymentSummary: ICartPaymentSummary | null;
  totalDiscountInCents: number;
}

export function ResumePaymentConditionsCard({
  payment,
  breakdown,
  cartSummaryCategories,
  paymentSummary,
  totalDiscountInCents,
}: ResumePaymentConditionsCardProps) {
  if (!payment) return null;

  const isCash = payment.paymentType === 'CASH';
  const summary = paymentSummary;
  const freightInCents = summary?.shippingInCents ?? payment.freteInCents ?? 0;
  const hasFreight = freightInCents > 0;
  const hasConnectivity = (breakdown?.connectivity.totalItems ?? 0) > 0;

  const conditionItems: ConditionItemData[] = [];

  if (isCash) {
    const cashTotalCents = summary?.cashTotal?.totalInCents ?? 0;
    conditionItems.push({
      key: 'cash',
      icon: <Banknote className="h-5 w-5 text-emerald-600" />,
      title: 'Pagamento à vista',
      value: cashTotalCents > 0 ? fmtCents(cashTotalCents) : 'À vista',
      info: payment.boletoAvista ? ['Pagamento por boleto'] : undefined,
    });
  } else {
    const hasEntrance = !!(payment.hasDownPayment && payment.downPaymentPercentage);
    const freightInEntrance = hasFreight && !!payment.shippingIncludedInDownPayment && hasEntrance;

    if (hasEntrance) {
      const entranceValue = summary?.installmentTotal?.downPaymentInCents ?? 0;
      const entranceInfo: string[] = [];
      if (freightInEntrance) entranceInfo.push(`Frete ${fmtCents(freightInCents)}`);
      if (payment.downPaymentByBankSlip) entranceInfo.push('Pagamento por boleto');
      if (payment.prorrogarEntrada) {
        const dateLabel = formatIsoDatePtBr(payment.entradaProrrogadaEm);
        if (dateLabel) entranceInfo.push(`Entrada prorrogada para ${dateLabel}`);
      }
      conditionItems.push({
        key: 'entrance',
        icon: <Percent className="h-5 w-5 text-amber-600" />,
        title: 'Entrada',
        value: `${payment.downPaymentPercentage}%${entranceValue > 0 ? ` · ${fmtCents(entranceValue)}` : ''}`,
        info: entranceInfo.length > 0 ? entranceInfo : undefined,
      });
    }

    if (payment.installments > 0) {
      const installmentValue = summary?.installmentTotal?.installmentBreakdown?.installmentInCents;
      const installmentInfo: string[] = [];
      if (payment.dueDay > 0) installmentInfo.push(`Vencimento dia ${payment.dueDay}`);
      conditionItems.push({
        key: 'installments',
        icon: <CreditCard className="h-5 w-5 text-violet-600" />,
        title: 'Parcelamento',
        value: `${payment.installments}x${installmentValue ? ` de ${fmtCents(installmentValue)}` : ''}`,
        info: installmentInfo.length > 0 ? installmentInfo : undefined,
      });
    }

    if (hasFreight && !freightInEntrance) {
      conditionItems.push({
        key: 'freight',
        icon: <Truck className="h-5 w-5 text-orange-600" />,
        title: 'Frete',
        value: fmtCents(freightInCents),
      });
    }
  }

  if (hasConnectivity && summary?.recurringConnectivity) {
    conditionItems.push({
      key: 'connectivity',
      icon: <Wifi className="h-5 w-5 text-blue-600" />,
      title: 'Conectividade',
      value: fmtCents(summary.recurringConnectivity.monthlyPaymentInCents),
      info: ['Mensal recorrente'],
    });
  }

  if (summary?.recurringMonthlyServices) {
    conditionItems.push({
      key: 'monthly-services',
      icon: <CalendarDays className="h-5 w-5 text-cyan-600" />,
      title: 'Serviços',
      value: fmtCents(summary.recurringMonthlyServices.monthlyPaymentInCents),
      info: ['Mensal recorrente'],
    });
  }

  if (summary?.recurringYearlyServices) {
    conditionItems.push({
      key: 'yearly-services',
      icon: <CalendarDays className="h-5 w-5 text-indigo-600" />,
      title: 'Serviços',
      value: fmtCents(summary.recurringYearlyServices.yearlyPaymentInCents),
      info: ['Anual recorrente'],
    });
  }

  const hasProducts = (breakdown?.products.totalItems ?? 0) > 0;
  const hasAccessories = (breakdown?.accessories.totalItems ?? 0) > 0;
  const showTotalAVista = isCash && !!summary?.cashTotal;
  const showTotalParcelado = !isCash && !!summary?.installmentTotal;
  const showRecurringConnectivity =
    (summary?.recurringConnectivity?.monthlyPaymentInCents ?? 0) > 0;
  const showRecurringMonthlyServices =
    (summary?.recurringMonthlyServices?.monthlyPaymentInCents ?? 0) > 0;
  const showRecurringYearlyServices =
    (summary?.recurringYearlyServices?.yearlyPaymentInCents ?? 0) > 0;

  const productsAccessoriesTotal =
    (hasProducts ? cartSummaryCategories.products.grossInCents : 0) +
    (hasAccessories ? cartSummaryCategories.accessories.grossInCents : 0);

  const installmentTotal = showTotalParcelado ? (summary?.installmentTotal?.totalInCents ?? 0) : 0;
  const cashTotal = showTotalAVista ? (summary?.cashTotal?.totalInCents ?? 0) : 0;
  const mainTotal = isCash ? cashTotal : installmentTotal;

  const recurringMonthly =
    (summary?.recurringConnectivity?.monthlyPaymentInCents ?? 0) +
    (summary?.recurringMonthlyServices?.monthlyPaymentInCents ?? 0);
  const recurringYearly = summary?.recurringYearlyServices?.yearlyPaymentInCents ?? 0;

  return (
    <div className="space-y-5">
      <ResumeCard>
        <ResumeCardHeader title="Condições de pagamento" />
        <div className="divide-y divide-gray-100 px-5">
          {conditionItems.map(({ key, ...item }) => (
            <ConditionItem key={key} {...item} />
          ))}
        </div>
      </ResumeCard>

      <ResumeCard>
        <ResumeCardHeader title="Resumo financeiro" />
        <div className="space-y-4 p-5">
          <div className="space-y-2.5">
            {productsAccessoriesTotal > 0 && (
              <SummaryRow label="Subtotal" value={fmtCents(productsAccessoriesTotal)} />
            )}
            {totalDiscountInCents > 0 && (
              <SummaryRow label="Descontos" value={`- ${fmtCents(totalDiscountInCents)}`} green />
            )}
            {hasFreight && <SummaryRow label="Frete" value={fmtCents(freightInCents)} />}
          </div>

          {mainTotal > 0 && (
            <div className="flex items-center justify-between rounded-lg bg-gray-900 px-4 py-3.5">
              <span className="text-sm font-semibold text-gray-300">Total</span>
              <span className="text-lg font-bold tabular-nums text-white">
                {fmtCents(mainTotal)}
              </span>
            </div>
          )}

          {(showRecurringConnectivity || showRecurringMonthlyServices) && recurringMonthly > 0 && (
            <>
              <Separator />
              <div className="space-y-2.5">
                <SummaryRow label="Total recorrente mensal" value={fmtCents(recurringMonthly)} />
                <SummaryRow label="Total" value={`${fmtCents(recurringMonthly)}/mês`} bold />
              </div>
            </>
          )}

          {showRecurringYearlyServices && recurringYearly > 0 && (
            <>
              <Separator />
              <div className="space-y-2.5">
                <SummaryRow label="Total recorrente anual" value={fmtCents(recurringYearly)} />
                <SummaryRow label="Total" value={`${fmtCents(recurringYearly)}/ano`} bold />
              </div>
            </>
          )}
        </div>
      </ResumeCard>
    </div>
  );
}
